import { useEffect } from 'react';
import {
  closeCommandPalette,
  openCommandPalette,
  toggleCommandPalette,
  useIsCommandPaletteOpen,
} from '../components/CommandPalette/commandPaletteStore';
import { isTypingTarget } from '../components/CommandPalette/isTypingTarget';

/**
 * The command palette state for the app shell, with its keys: ⌘K / Ctrl+K toggles it and "/" opens
 * it (design navigation-v2 e 5d). "/" types a slash in a field, and a page that uses "/" itself
 * (the Search page focuses its box) prevents the default first. Any other component opens the
 * palette with `openCommandPalette()`. With `enabled` false (a public page for a signed-out
 * visitor, the native web view: no navigation is drawn) the keys do nothing and the palette stays
 * closed.
 */
export const useCommandPalette = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const isOpen = useIsCommandPaletteOpen() && enabled;

  useEffect(() => {
    // A signed-out visitor of a public page and the native web view have no app search.
    if (!enabled) {
      closeCommandPalette();
      return undefined;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        toggleCommandPalette();
        return;
      }
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.defaultPrevented && !isTypingTarget(e.target)) {
        e.preventDefault();
        openCommandPalette();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled]);

  return { isOpen, open: openCommandPalette, close: closeCommandPalette };
};
